import { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { API } from "@/App";
import { 
  LayoutDashboard, 
  Users, 
  Building2,
  LogOut,
  Menu,
  X,
  Shield,
  IndianRupee,
  UserCog,
  UserCheck,
  ShieldCheck,
  LifeBuoy,
  Settings,
  ScrollText,
  Mail,
  Flag,
  LibraryBig,
  CreditCard,
  BookOpen,
  DatabaseZap,
  Database,
  FileQuestion,
  MessageSquare,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdminEnvProvider, EnvBadge } from "@/components/admin/EnvContext";

export default function AdminLayout() {
  const [admin, setAdmin] = useState(null);
  const [environment, setEnvironment] = useState("preview");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchAdmin = useCallback(async () => {
    try {
      // Authentication is handled via HttpOnly cookies (secure against XSS)
      // No need to check localStorage - cookies are sent automatically
      const response = await fetch(`${API}/admin/auth/me`, {
        credentials: "include", // This sends HttpOnly cookies
      });
      
      // Clone response to handle potential stream issues
      let data = {};
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails but status is OK, token might still be valid
        if (response.ok) {
          console.warn("Admin auth response parsing failed, but status OK");
          setLoading(false);
          return; // Don't redirect, let page load
        }
      }

      if (response.ok && data.admin) {
        setAdmin(data.admin);
        if (data.environment) setEnvironment(data.environment);
      } else if (response.status === 401) {
        // Not authenticated - redirect to login
        navigate("/admin");
      } else if (!response.ok) {
        // Other errors - redirect to login
        navigate("/admin");
      }
    } catch (error) {
      console.error("Admin auth check error:", error);
      // Network error - redirect to login
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAdmin();
  }, [fetchAdmin]);

  const handleLogout = async () => {
    try {
      // Logout endpoint will clear the HttpOnly cookies on the server side
      await fetch(`${API}/admin/auth/logout`, {
        method: "POST",
        credentials: "include", // Send cookies for logout
      });
      
      toast.success("Logged out successfully");
      setShowLogoutModal(false);
      navigate("/");
    } catch (error) {
      // Even if API call fails, redirect to login
      toast.error("Logout failed");
      setShowLogoutModal(false);
      navigate("/");
    }
  };

  const confirmLogout = () => {
    setShowLogoutModal(true);
  };

  // Role-gated navigation: each tab declares the permission(s) that unlock it.
  // A tab shows if the user is super_admin, OR holds any listed permission.
  // Tabs with `superOnly` are only ever shown to super admins (no catalog permission
  // represents them). The same access is enforced server-side on each endpoint.
  const isSuperAdmin = admin?.role === "super_admin";
  const perms = admin?.effective_permissions || [];
  const can = (...needed) => isSuperAdmin || needed.some((p) => perms.includes(p));

  const allNav = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, show: can("dashboard:read") },
    { path: "/admin/parents", label: "Parents", icon: Users, show: can("parents:read") },
    { path: "/admin/students", label: "Students", icon: UserCheck, show: can("students:read") },
    { path: "/admin/teachers", label: "Teachers", icon: UserCog, show: can("teachers:read") },
    { path: "/admin/schools", label: "Schools", icon: Building2, show: can("schools:read") },
    { path: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard, show: can("subscriptions:read") },
    { path: "/admin/finance", label: "Finance", icon: IndianRupee, show: can("finance:read") },
    { path: "/admin/admins", label: "Users", icon: UserCog, show: can("admins:read") },
    { path: "/admin/roles", label: "Roles & Permissions", icon: ShieldCheck, show: can("roles:read") },
    { path: "/admin/tickets", label: "Support Tickets", icon: LifeBuoy, show: can("support:tickets:read") },
    { path: "/admin/settings", label: "Settings", icon: Settings, show: can("settings:read") },
    { path: "/admin/audit", label: "Audit Logs", icon: ScrollText, show: can("audit:read") },
    { path: "/admin/emails", label: "Email Templates", icon: Mail, superOnly: true },
    { path: "/admin/curriculum", label: "Curriculum", icon: LibraryBig, show: can("content:read") },
    { path: "/admin/content-editor", label: "Content Editor", icon: BookOpen, show: can("content:read") },
    { path: "/admin/migration", label: "Data Migration", icon: DatabaseZap, superOnly: true },
    { path: "/admin/records", label: "Records", icon: Database, superOnly: true },
    { path: "/admin/question-bank", label: "Question Bank", icon: FileQuestion, show: can("content:read") },
    { path: "/admin/whatsapp-logs", label: "WhatsApp Logs", icon: MessageSquare, superOnly: true },
  ];

  const navByPath = Object.fromEntries(allNav.map((i) => [i.path, i]));
  const isVisible = (path) => {
    const it = navByPath[path];
    return !!it && (it.superOnly ? isSuperAdmin : it.show);
  };

  // Two frequently-used destinations stay pinned at the top (no group).
  const pinnedPaths = ["/admin/dashboard", "/admin/tickets"];
  const pinnedItems = pinnedPaths.filter(isVisible).map((p) => navByPath[p]);

  // Grouped (collapsible) sections. A group renders only if it has ≥1 visible item.
  const groupDefs = [
    { key: "people", label: "People", icon: Users, paths: ["/admin/parents", "/admin/students", "/admin/teachers", "/admin/schools"] },
    { key: "billing", label: "Billing", icon: CreditCard, paths: ["/admin/subscriptions", "/admin/finance"] },
    { key: "content", label: "Content", icon: LibraryBig, paths: ["/admin/curriculum", "/admin/content-editor", "/admin/question-bank"] },
    { key: "access", label: "Access Control", icon: ShieldCheck, paths: ["/admin/admins", "/admin/roles"] },
    { key: "logs", label: "Logs", icon: ScrollText, paths: ["/admin/whatsapp-logs", "/admin/audit"] },
    { key: "system", label: "System", icon: Settings, paths: ["/admin/settings", "/admin/emails", "/admin/migration", "/admin/records"] },
  ];
  const groups = groupDefs
    .map((g) => ({ ...g, items: g.paths.filter(isVisible).map((p) => navByPath[p]) }))
    .filter((g) => g.items.length > 0);

  // Which group holds the current page? That group auto-expands.
  const activeGroupKey = groups.find((g) => g.items.some((it) => it.path === location.pathname))?.key || null;

  // Auto-expand the active page's group on navigation (one group open at a time).
  useEffect(() => {
    setOpenGroup(activeGroupKey);
  }, [activeGroupKey]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-slate-800">Eduzy Admin</span>
          <EnvBadge environment={environment} />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-white border-r border-slate-200
            transform transition-transform duration-300 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="hidden lg:flex items-center gap-3 px-6 py-5 border-b border-slate-200">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-800">Eduzy</h1>
                <p className="text-xs text-slate-500">Admin Portal</p>
              </div>
              <div className="ml-auto">
                <EnvBadge environment={environment} />
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
              {/* Pinned items */}
              {pinnedItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.path.split("/").pop()}`}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors
                      ${isActive
                        ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                        : "text-slate-600 hover:bg-slate-100"
                      }
                    `}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              {/* Grouped sections */}
              {groups.map((group) => {
                const GroupIcon = group.icon;
                const isOpen = openGroup === group.key;
                const hasActive = group.key === activeGroupKey;
                return (
                  <div key={group.key} className="pt-2">
                    <button
                      type="button"
                      onClick={() => setOpenGroup((prev) => (prev === group.key ? null : group.key))}
                      data-testid={`nav-group-${group.key}`}
                      className={`
                        w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide
                        transition-colors
                        ${hasActive && !isOpen ? "text-blue-600" : "text-slate-400 hover:text-slate-600"}
                      `}
                    >
                      <GroupIcon size={16} className={hasActive && !isOpen ? "text-blue-500" : ""} />
                      <span className="flex-1 text-left">{group.label}</span>
                      {hasActive && !isOpen && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" aria-hidden="true" />
                      )}
                      <ChevronDown
                        size={15}
                        className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-200 ease-in-out ${isOpen ? "max-h-96 mt-1" : "max-h-0"}`}
                    >
                      <div className="space-y-1 pl-2">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`nav-${item.path.split("/").pop()}`}
                              className={`
                                flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium transition-colors
                                ${isActive
                                  ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                                  : "text-slate-600 hover:bg-slate-100"
                                }
                              `}
                            >
                              <Icon size={18} />
                              <span>{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </nav>

            {/* User Section */}
            <div className="p-4 border-t border-slate-200">
              <div className="flex items-center gap-3 px-3 py-2 mb-2">
                <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-slate-600">
                    {admin.name?.charAt(0) || "A"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {admin.name}
                  </p>
                  <p className="text-xs text-slate-500 truncate capitalize">
                    {admin.role?.replace("_", " ")}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={confirmLogout}
                data-testid="admin-logout-button"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 lg:pt-6 min-h-screen">
          <AdminEnvProvider environment={environment}>
            <Outlet context={{ admin, environment }} />
          </AdminEnvProvider>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      <Dialog open={showLogoutModal} onOpenChange={setShowLogoutModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5 text-orange-600" />
              Confirm Logout
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to logout from the Admin Portal?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setShowLogoutModal(false)} data-testid="cancel-logout-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLogout} data-testid="confirm-logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Yes, Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
